import {
    getScopeExtractedFields,
    IExtractedCertificateOfInsurance,
    IScope,
} from "@/api";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Box, Stack, Typography } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { FoundToggleState } from "../../types";
import { CertificateOfInsurance } from "./CertificateOfInsurance";
import { PolicyTypeFieldsSection } from "./PolicyTypeFieldsSection";

interface IProps {
    scope: IScope;
    search: string;
    foundToggle: FoundToggleState;
    pageNavigationPluginInstance: PageNavigationPlugin;
    coi?: IExtractedCertificateOfInsurance | null;
    currentPage: number;
    totalPages: number;
}

export const Fields = ({
    scope,
    search,
    foundToggle,
    pageNavigationPluginInstance,
    coi,
    currentPage,
    totalPages,
}: IProps) => {
    const isFoundState = foundToggle === FoundToggleState.FOUND;

    const { data: extractedFieldsData, isLoading: isLoadingExtractedFields } =
        useQuery({
            queryKey: ["scope-extracted-fields", scope.id],
            queryFn: () => getScopeExtractedFields(scope.id),
            enabled: !!scope.id,
        });

    const hasExtractedFields = useMemo(() => {
        return (extractedFieldsData?.field_groups?.length ?? 0) > 0;
    }, [extractedFieldsData?.field_groups]);

    const extractedFieldsHasVisibleContent = useMemo(() => {
        if (!extractedFieldsData?.field_groups) return false;

        return extractedFieldsData.field_groups.some((group) => {
            const hasScalarFields = group.scalar_fields.some((field) => {
                const hasValue = field.items.some((item) =>
                    item.properties.some((p) => p.value !== null)
                );
                if (isFoundState && !hasValue) return false;
                if (!isFoundState && hasValue) return false;
                if (search) {
                    const searchLower = search.toLowerCase();
                    const nameMatches = field.field_name
                        .toLowerCase()
                        .includes(searchLower);
                    const valueMatches = field.items.some((item) =>
                        item.properties.some(
                            (p) =>
                                p.value &&
                                p.value.toLowerCase().includes(searchLower)
                        )
                    );
                    if (!nameMatches && !valueMatches) return false;
                }
                return true;
            });

            const hasArrayFields = group.array_fields.some((field) => {
                const hasItems = field.items.length > 0;
                if (isFoundState && !hasItems) return false;
                if (!isFoundState && hasItems) return false;
                if (search) {
                    const searchLower = search.toLowerCase();
                    const nameMatches = field.field_name
                        .toLowerCase()
                        .includes(searchLower);
                    const itemMatches = field.items.some((item) => {
                        const displayNameMatches =
                            item.display_name &&
                            item.display_name
                                .toLowerCase()
                                .includes(searchLower);
                        const propertyMatches = item.properties.some(
                            (p) =>
                                p.value &&
                                p.value.toLowerCase().includes(searchLower)
                        );
                        return displayNameMatches || propertyMatches;
                    });
                    if (!nameMatches && !itemMatches) return false;
                }
                return true;
            });

            // Custom fields are user-created — always count as "found".
            // After Slice 6, standalone custom fields render inline via scalar_fields,
            // but raw data still has them in custom_fields.
            const hasCustomFields =
                foundToggle !== FoundToggleState.NOT_FOUND &&
                (group.custom_fields ?? []).some((cf) => {
                    if (search) {
                        const searchLower = search.toLowerCase();
                        const nameMatches = cf.name
                            .toLowerCase()
                            .includes(searchLower);
                        const valueMatches = cf.properties?.some(
                            (p) =>
                                p.name?.toLowerCase().includes(searchLower) ||
                                p.value?.toLowerCase().includes(searchLower)
                        );
                        return nameMatches || valueMatches;
                    }
                    return true;
                });

            return hasScalarFields || hasArrayFields || hasCustomFields;
        });
    }, [extractedFieldsData?.field_groups, search, isFoundState, foundToggle]);

    const hasVisibleCoi = !!coi && isFoundState;
    const hasAnyVisibleContent =
        extractedFieldsHasVisibleContent || hasVisibleCoi;

    if (isLoadingExtractedFields) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <LoadingIndicator size={20} />
            </Box>
        );
    }

    if (!hasExtractedFields && !coi) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ p: 2 }}>
                <Typography color="text.secondary">
                    No fields exist for this scope.
                </Typography>
            </Stack>
        );
    }

    if (!hasAnyVisibleContent) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ p: 2 }}>
                <SearchOffIcon
                    sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No results found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {search && `No fields match "${search}"`}
                    {!search &&
                        foundToggle === FoundToggleState.NOT_FOUND &&
                        "No missing fields"}
                    {!search &&
                        foundToggle === FoundToggleState.FOUND &&
                        "No found fields"}
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack>
            {hasExtractedFields && (
                <PolicyTypeFieldsSection
                    scope={scope}
                    search={search}
                    foundToggle={foundToggle}
                    pageNavigationPluginInstance={pageNavigationPluginInstance}
                    currentPage={currentPage}
                    totalPages={totalPages}
                />
            )}

            {coi && isFoundState && (
                <CertificateOfInsurance
                    coi={coi}
                    pageNavigationPluginInstance={pageNavigationPluginInstance}
                />
            )}
        </Stack>
    );
};
